import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { signOrderAgreement, getOrderForCurrentBuyer } from "@/lib/store.functions";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/store/agreement/$orderId")({
  component: AgreementPage,
});

function AgreementPage() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const sign = useServerFn(signOrderAgreement);
  const fetchOrder = useServerFn(getOrderForCurrentBuyer);
  const { data } = useQuery({
    queryKey: ["store", "order", orderId],
    queryFn: () => fetchOrder({ data: { orderId } }),
    enabled: !!user && !authLoading,
    retry: false,
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const alreadySigned = data?.agreementSigned;

  if (!authLoading && !user) {
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 py-24 text-center">
          <Eyebrow align="center">Sign in required</Eyebrow>
          <h1 className="mt-6 font-serif text-4xl text-navy-900">
            Sign in to view this agreement
          </h1>
          <p className="mt-6 font-sans text-charcoal-900">
            Agreements are tied to the account that placed the order. Sign in
            with that account to review and sign.
          </p>
          <div className="mt-10">
            <a href={`/login?redirect=/store/agreement/${orderId}`}>
              <PrimaryButton>Sign in</PrimaryButton>
            </a>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ack) { toast.error("Please acknowledge the Member Agreement to continue."); return; }
    setLoading(true);
    try {
      await sign({ data: { orderId, signedName: name, signedEmail: email, acknowledged: true } });
      toast.success("Agreement signed. Your library will unlock within 24 hours.");
      navigate({ to: "/store/library" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Signing failed");
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <section className="mx-auto max-w-2xl px-6 py-20">
        <Eyebrow>Member Agreement</Eyebrow>
        <h1 className="mt-4 font-serif text-4xl text-navy-900">Acknowledge and sign</h1>
        {alreadySigned ? (
          <p className="mt-8 rounded-sm border border-bronze-600/30 bg-white p-6 font-sans text-charcoal-900">
            This agreement has already been signed. Your library will be delivered within 24 hours.
          </p>
        ) : (
          <>
            <div className="mt-8 max-h-72 overflow-y-auto rounded-sm border border-bronze-600/30 bg-white p-6 font-sans text-sm leading-relaxed text-charcoal-900">
              <p><strong>Member Agreement</strong></p>
              <p className="mt-4">By signing below, you affirm your membership in the Currency Thoughts study community.</p>
              <p className="mt-4"><strong>License.</strong> Digital materials are licensed for your personal household use. Please do not redistribute, resell, or share files outside your household.</p>
              <p className="mt-4"><strong>Refunds.</strong> Digital purchases are final once the materials have been delivered, except where a refund is required by law. If something is wrong with your order, contact us and we will make it right.</p>
              <p className="mt-4"><strong>Delivery.</strong> Your materials are available in your library immediately after purchase.</p>
            </div>
            <form onSubmit={onSubmit} className="mt-8 space-y-6">
              <div>
                <label className="font-sans text-sm text-charcoal-900">Full legal name</label>
                <Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-2" />
              </div>
              <div>
                <label className="font-sans text-sm text-charcoal-900">Email</label>
                <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
              </div>
              <label className="flex items-start gap-3 font-sans text-sm text-charcoal-900">
                <Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} />
                <span>I have read and agree to the Member Agreement.</span>
              </label>
              <PrimaryButton type="submit" disabled={loading || !ack}>
                {loading ? "Signing…" : "Sign and unlock my library"}
              </PrimaryButton>
            </form>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}